/**
 * ChatEngineLoader — the chat engine arrives a moment after the page paints
 * (Miles, 2026-09-22: "slim the app first"; "let it arrive, then land it").
 *
 * WHY. Every signed-out visitor to prest0.ai used to download the whole chat
 * engine — typing presence, live-turn cards, mention pills, the thread store —
 * before the homepage headline could paint, because App.tsx imported it
 * eagerly. It is now its own chunk, `chat-engine`, requested in the first
 * effect after mount: the headline paints first, and the closed chat bar with
 * the California disclosure line under it ("Prest0n is a digital AI employee,
 * not a person.", Cal. B&P §17941) arrives together a moment later. The
 * disclosure is part of the engine, so it can never be absent while the
 * composer is present.
 *
 * WHY NOT React.lazy. App.tsx records React.lazy stalling on first mount of
 * `/` in production and being removed route by route; the root cause was
 * never found. This uses useDeferredModule — a plain import() from an effect,
 * resolved into state — so no Suspense boundary exists to stall, and a failed
 * download is a state value, never a throw: it cannot blank the homepage.
 *
 * WHAT THE LOADER DOES BEFORE THE ENGINE EXISTS, so nothing about the wait is
 * visible beyond the bar arriving:
 *   - stamps the page with the chat's saved mode (docked beside the page;
 *     full screen) from the same reading the engine's reducer starts from
 *     (bootChatMode.ts), so a returning visitor's layout does not shift when
 *     the engine lands, and does not clear it at hand-off — the engine
 *     publishes the same values from its first frame;
 *   - on a phone whose chat was open, holds an inert backdrop where the
 *     full-screen chat will be, instead of showing the landing page and then
 *     covering it (never over a page the chat will stand under: chatBelow.ts),
 *     in the light the chat will open in (D-92);
 *   - holds a tile's "open the chat" and a notification tap until the engine
 *     is listening (chatIntentBuffer.ts).
 *
 * A download that fails is retried (useDeferredModule → retryImport, then the
 * stale-tab self-heal for a chunk that is gone for good), and again each time
 * the browser comes back online, a bounded number of times. Anything held is
 * never left waiting on a retry that cannot come: an online browser gets no
 * "back online" event, so a failure there delivers what was held the way it
 * is delivered with no chat at all (tests/landing/chatEngineLoaderHeldIntents
 * .test.tsx). Measured in the field: the `prest0:chat-engine-mounted`
 * performance mark (chatEngineMark.ts) rides the first-party web-vitals beacon
 * as CHAT_READY (src/reportWebVitals.ts).
 *
 * Pinned by tests/landing/chatEngineLoader.test.tsx and
 * tests/landing/chatEngineOffFirstPaint.test.ts; kept off the first download
 * by scripts/check-chat-first-paint.mjs.
 */

import React, { useEffect, useLayoutEffect, useState } from 'react';
import type { GlobalChatConfig } from './types';
import type { GlobalChatSystem as ChatEngineComponent } from './GlobalChatSystem';
import { useDeferredModule } from '../../utils/useDeferredModule';
import { readBootChatMode, bootPageSignals } from './bootChatMode';
import { useChatBelowRequest } from './chatBelow';
import { applyChatModeAttribute } from './chatModeSignal';
import { setChatPanelFullScreen } from '../wallet/chatPanelStore';
import {
  installChatIntentBuffer,
  drainChatIntentBuffer,
  releaseChatIntentBufferWithoutChat,
} from './chatIntentBuffer';
import { CHAT_FULLSCREEN_Z_INDEX } from '../../styles/layers';
import { CHAT_ENGINE_MOUNTED_MARK } from './chatEngineMark';
import { semanticDark } from '../../styles/semanticTokens.generated';
import { designShows } from '../../design/previewPath';
import { resolvePreviewTheme } from '../../design/previewTheme';

interface ChatEngineModule {
  GlobalChatSystem: typeof ChatEngineComponent;
}

/** The ONE dynamic import of the engine (the chat-first-paint gate counts it). */
const loadChatEngine = (): Promise<ChatEngineModule> =>
  import(/* webpackChunkName: "chat-engine" */ './GlobalChatSystem');

/** Reloads attempted when the browser reports it is back online after a failure. */
export const CHAT_ENGINE_ONLINE_RETRIES = 3;

/** Re-exported from its leaf (chatEngineMark.ts), where the field reporter reads it too. */
export { CHAT_ENGINE_MOUNTED_MARK };

/** `navigator.onLine` is false only when the browser KNOWS it is offline. */
function browserIsOnline(): boolean {
  try {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  } catch {
    return true;
  }
}

export interface ChatEngineLoaderProps {
  config: Partial<GlobalChatConfig>;
}

export const ChatEngineLoader: React.FC<ChatEngineLoaderProps> = ({ config }) => {
  const [attempt, setAttempt] = useState(0);
  const { module, error } = useDeferredModule(loadChatEngine, `chat-engine:${attempt}`, { start: 'mount' });
  // Read once, at the loader's first render — the same storage the engine's
  // reducer reads on its own first render.
  const [bootMode] = useState(() => readBootChatMode(config));

  const engineReady = Boolean(module);
  const failed = Boolean(error) && !engineReady;
  // A phone chat stands under a page that asks for it (/desktop; chatBelow.ts): no takeover, so no backdrop over it.
  const belowRequest = useChatBelowRequest();
  const signals = bootPageSignals(bootMode, Boolean(config.hidden), bootMode.isMobileViewport && Boolean(belowRequest?.fits));

  useLayoutEffect(() => {
    installChatIntentBuffer();
  }, []);

  // Before the engine exists, the page carries its saved mode; once it exists
  // the engine owns both facts (from its first frame on), so this stops.
  useLayoutEffect(() => {
    if (engineReady) return;
    applyChatModeAttribute(!failed && signals.docked);
    setChatPanelFullScreen(!failed && signals.fullScreen);
  }, [engineReady, failed, signals.docked, signals.fullScreen]);

  // Once per FAILED LOAD — keyed on the error itself, never on `attempt`: the
  // render that bumps `attempt` still carries the previous error (the hook
  // clears it in its own effect), and acting on that stale value logged twice
  // and released what was held before the retry had even run.
  useEffect(() => {
    if (!failed) return undefined;
    console.error('[chat] the chat engine failed to load', error);
    const retriesLeft = attempt < CHAT_ENGINE_ONLINE_RETRIES;
    // The only retry left is "when the browser comes back online". A browser
    // that is online already sends no such event, so anything held — a
    // notification tap above all — would wait forever: deliver it now, the way
    // it is delivered with no chat at all. Offline keeps holding: a page load
    // would only reach the browser's offline page.
    if (!retriesLeft || browserIsOnline()) releaseChatIntentBufferWithoutChat();
    if (!retriesLeft) return undefined;
    const onOnline = () => setAttempt((n) => n + 1);
    window.addEventListener('online', onOnline, { once: true });
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the note above
  }, [error]);

  if (module) {
    const Engine = module.GlobalChatSystem;
    return (
      <>
        <Engine config={config} />
        <ChatEngineArrived />
      </>
    );
  }
  if (!failed && signals.fullScreen) {
    // The full-screen chat takes the viewer's own light from its first frame (D-70; GlobalChatSystem's
    // publishChatLight), so the place held for it is already in that light: never a noon frame before a dusk chat (D-92).
    const dusk = designShows('chat') && resolvePreviewTheme() === 'dark';
    return (
      <div
        aria-hidden="true"
        data-testid="chat-engine-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: CHAT_FULLSCREEN_Z_INDEX,
          background: dusk ? semanticDark.canvas : 'var(--brand-canvas)',
        }}
      />
    );
  }
  return null;
};

/**
 * Rendered AFTER the engine, as its sibling: React runs a commit's effects
 * child-first and in sibling order, so by the time this effect runs the
 * engine's own mount effects — the listeners the held requests go to — have
 * run. Marks the moment for field measurement, then delivers what was held.
 */
const ChatEngineArrived: React.FC = () => {
  useEffect(() => {
    try {
      performance.mark(CHAT_ENGINE_MOUNTED_MARK);
    } catch {
      /* no User Timing API */
    }
    drainChatIntentBuffer();
  }, []);
  return null;
};

export default ChatEngineLoader;
